import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Form,
  Input,
  Button,
  Descriptions,
  Typography,
  message,
  Modal,
  Drawer,
  Divider,
} from "antd";
import { ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";

import {
  UPDATE_PROJECT_MUTATION,
  PROJECTS_SIMPLIFIED_QUERY,
  GENERATE_PROJECT_SECRET,
  GENERATE_PROJECT_API_TOKEN_MUTATION,
} from "../../helpers/graphql/project";
import { PortalApiDoc } from "../drawer/PortalApiDoc";
import { API_BASEURL } from "../../config/StaticConfig";

const { confirm } = Modal;

interface Props {
  project: {
    id: string;
    identifier: string;
    name: string;
    secret: string;
    apiToken: string;
    createdAt: string;
  };
}

export function ProjectDetails({ project }: Props) {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);

  const [generateProjectAPIToken, { loading: apiLoading }] = useMutation(
    GENERATE_PROJECT_API_TOKEN_MUTATION,
    {
      variables: { projectId: project.id },
      onCompleted: ({ generateProjectAPIToken }) => {
        if (generateProjectAPIToken) {
          message.success("Generated Project API token!");
        }
      },
      refetchQueries: () => [
        {
          query: PROJECTS_SIMPLIFIED_QUERY,
        },
      ],
    },
  );
  const [updateProject, { loading }] = useMutation(UPDATE_PROJECT_MUTATION, {
    onCompleted: ({ updateProject }) => {
      if (updateProject) {
        message.success("Saved changes!");
      }
    },
    refetchQueries: () => [
      {
        query: PROJECTS_SIMPLIFIED_QUERY,
      },
    ],
  });

  const [generateSecret, { loading: secretLoading }] = useMutation(GENERATE_PROJECT_SECRET, {
    onCompleted: ({ generateSecret }) => {
      if (generateSecret) {
        message.success("Regenerate project secret!");
      }
    },
    refetchQueries: () => [
      {
        query: PROJECTS_SIMPLIFIED_QUERY,
      },
    ],
  });

  const handleSubmit = (values: { name: string }) => {
    const variables = {
      id: project.id,
      ...values,
    };
    updateProject({ variables });
  };

  const showAPITokenConfirm = () => {
    confirm({
      title: "Are you sure to generate new API token?",
      icon: <ExclamationCircleOutlined />,
      content:
        "The existing API token will be de-activated and need to update your application auth header.",
      okText: "Yes",
      okType: "primary",
      cancelText: "cancel",
      onOk() {
        generateProjectAPIToken();
      },
    });
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  return (
    <>
      <Form
        form={form}
        name="update-project"
        initialValues={project}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginBottom: 50 }}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save
          </Button>
        </Form.Item>
      </Form>

      <Descriptions title="Project Info" layout="horizontal" column={1}>
        <Descriptions.Item label="Project ID">
          <Typography.Text code copyable>
            {project.id}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Project secret">
          {project.secret ? (
            <Typography.Text code copyable>
              {project.secret}
            </Typography.Text>
          ) : null}
          <Button
            type="primary"
            size="small"
            loading={secretLoading}
            onClick={() => generateSecret({ variables: { projectId: project.id } })}
          >
            Re-generate
          </Button>
        </Descriptions.Item>
        <Descriptions.Item label="Identifier">
          <Typography.Text code copyable>
            {project.identifier}
          </Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Descriptions
        title={
          <>
            Portal API
            <Button type="ghost" size="small" style={{ marginLeft: 10 }} onClick={showDrawer}>
              <InfoCircleOutlined />
              Documentation
            </Button>
          </>
        }
        layout="horizontal"
        column={1}
      >
        <Descriptions.Item label="API endpoint">
          {project.apiToken ? (
            <Typography.Text code copyable>
              {`${API_BASEURL}/api/`}
            </Typography.Text>
          ) : null}
        </Descriptions.Item>
        <Descriptions.Item label="API Token">
          {project.apiToken ? (
            <Typography.Text code copyable>
              {project.apiToken}
            </Typography.Text>
          ) : null}
          <Button
            type="primary"
            size="small"
            loading={apiLoading}
            onClick={() => {
              project.apiToken ? showAPITokenConfirm() : generateProjectAPIToken();
            }}
          >
            {project.apiToken ? "Re-generate" : "Generate"}
          </Button>
        </Descriptions.Item>
      </Descriptions>

      <Drawer
        title="Portal API Doc"
        placement="right"
        onClose={onClose}
        visible={visible}
        size={"large"}
      >
        <PortalApiDoc apiToken={project.apiToken} />
      </Drawer>
    </>
  );
}
